import { Code, FileText } from 'lucide-react';
import { useState } from 'react';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { AcmBlock, AcmColumn } from '../types/kpi.js';
import { KPI_DEFINITIONS, type KpiMetricDefinition } from '../lib/kpi-definitions.js';

/** @graph-connects none */
type DefinitionTab = 'business' | 'technical';

/** @graph-connects none */
type MergedDefinition = AcmColumn & KpiMetricDefinition;

/** @graph-connects none */
const BLOCK_ORDER: readonly AcmBlock[] = ['会員数', 'スタイリング', 'チケット・返却', '集客'];

/** @graph-connects none */
const BLOCK_TINT: Record<AcmBlock, string> = {
  会員数: 'var(--color-accent-primary)',
  スタイリング: 'var(--color-text-secondary)',
  'チケット・返却': 'var(--color-text-muted)',
  集客: 'var(--color-accent-secondary)',
};

/** @graph-connects none */
const BLOCK_DB_SUMMARY: Record<AcmBlock, string> = {
  会員数: 'aircloset_mens (PostgreSQL)',
  スタイリング: 'phoenix_styling (MySQL)',
  'チケット・返却': 'aircloset_mens (PostgreSQL) / phoenix_order (MySQL)',
  集客: 'GA4 (ac_mens_hub.ga4__*)',
};

/** @graph-connects none */
function MetricBadge({ type }: { type: 'stock' | 'flow' }) {
  const isStock = type === 'stock';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
        isStock
          ? 'bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]'
          : 'bg-[var(--color-text-muted)]/10 text-[var(--color-text-secondary)]'
      }`}
    >
      {isStock ? 'ストック' : 'フロー'}
    </span>
  );
}

/** @graph-connects none */
function BusinessCard({ def }: { def: MergedDefinition }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] p-4">
      <div className="flex items-center gap-2">
        <span className="font-medium text-[var(--color-text-primary)]">{def.label}</span>
        <MetricBadge type={def.type} />
      </div>
      <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
        {def.businessDescription}
      </p>
      {def.validation && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          <span className="font-medium">検証ルール:</span> {def.validation}
        </p>
      )}
    </div>
  );
}

/** @graph-connects none */
function TechnicalCard({ def }: { def: MergedDefinition }) {
  return (
    <div className="rounded-lg border border-[var(--color-border)] p-4">
      <div className="flex items-center gap-2">
        <span className="font-medium text-[var(--color-text-primary)]">{def.label}</span>
        <MetricBadge type={def.type} />
        <code className="ml-auto rounded bg-[var(--color-bg-subtle)] px-1.5 py-0.5 text-xs text-[var(--color-text-muted)]">
          {def.key}
        </code>
      </div>
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-xs font-medium text-[var(--color-text-muted)]">ソースDB</dt>
        <dd className="font-mono text-xs text-[var(--color-text-secondary)]">
          {def.technicalSource.database}
        </dd>
        {def.technicalSource.tables.length > 0 && (
          <>
            <dt className="text-xs font-medium text-[var(--color-text-muted)]">テーブル</dt>
            <dd className="font-mono text-xs text-[var(--color-text-secondary)]">
              {def.technicalSource.tables.join(', ')}
            </dd>
          </>
        )}
        <dt className="self-start text-xs font-medium text-[var(--color-text-muted)]">
          抽出ロジック
        </dt>
        <dd className="text-xs leading-relaxed text-[var(--color-text-secondary)]">
          <pre className="whitespace-pre-wrap font-mono">{def.technicalSource.logic}</pre>
        </dd>
      </dl>
      {def.validation && (
        <p className="mt-3 border-t border-[var(--color-border)] pt-2 text-xs text-[var(--color-text-muted)]">
          <span className="font-medium">整合性検証:</span> {def.validation}
        </p>
      )}
    </div>
  );
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 日次KPIの指標定義ビューア。ビジネス定義（考え方・意味）と技術定義（DB・テーブル・SQLロジック）をタブで切り替えて表示する。
 * @graph-connects KPI_DEFINITIONS [references, via:key] 指標定義データを参照する
 */
export function KpiDefinitionView({ columns }: { columns: AcmColumn[] }) {
  const [tab, setTab] = useState<DefinitionTab>('business');

  const grouped = BLOCK_ORDER.map((block) => ({
    block,
    metrics: columns
      .filter((col) => col.block === block)
      .map((col): MergedDefinition => ({ ...col, ...KPI_DEFINITIONS[col.key] })),
  }));

  return (
    <div className="mt-6 space-y-6">
      <Card className="flex items-center gap-3 p-4">
        <div className="flex rounded-lg border border-[var(--color-border)]">
          <button
            type="button"
            onClick={() => {
              setTab('business');
            }}
            className={`flex items-center gap-1 rounded-l-lg px-3 py-2 text-xs transition-colors ${
              tab === 'business'
                ? 'bg-[var(--color-accent-primary)] text-white'
                : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
            }`}
          >
            <FileText className="h-3.5 w-3.5" aria-hidden="true" />
            ビジネス定義
          </button>
          <button
            type="button"
            onClick={() => {
              setTab('technical');
            }}
            className={`flex items-center gap-1 rounded-r-lg px-3 py-2 text-xs transition-colors ${
              tab === 'technical'
                ? 'bg-[var(--color-accent-primary)] text-white'
                : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
            }`}
          >
            <Code className="h-3.5 w-3.5" aria-hidden="true" />
            技術定義
          </button>
        </div>
        <p className="text-xs text-[var(--color-text-muted)]">
          {tab === 'business'
            ? '各指標の意味と考え方を説明しています'
            : 'ソースDB・テーブル・抽出SQLを記載しています'}
        </p>
      </Card>

      {tab === 'technical' && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            データパイプライン概要
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-[var(--color-text-secondary)]">
            3つの本番DB（aircloset_mens / phoenix_styling / phoenix_order）から毎日 06:00 JST に
            Cloud Run バッチが全期間を再計算し、BigQuery テーブル{' '}
            <code className="rounded bg-[var(--color-bg-subtle)] px-1 py-0.5">
              ac_mens_hub.kpi__daily
            </code>{' '}
            へ
            MERGE（upsert）で書き込みます。全期間を毎回再計算するため自己修復・冪等です。書き込み前に整合性バリデーション（恒等式・単調性・日付連続性）を実行し、違反があれば書き込みを中止します。
          </p>
          <div className="mt-3 flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
            <span className="rounded bg-[var(--color-bg-subtle)] px-2 py-1 font-mono">
              aircloset_mens_prod
            </span>
            <span className="rounded bg-[var(--color-bg-subtle)] px-2 py-1 font-mono">
              phoenix_styling_prod
            </span>
            <span className="rounded bg-[var(--color-bg-subtle)] px-2 py-1 font-mono">
              phoenix_order_prod
            </span>
            <span>→</span>
            <span className="rounded bg-[var(--color-bg-subtle)] px-2 py-1 font-mono">
              ac_mens_hub.kpi__daily
            </span>
            <span>→</span>
            <span className="font-medium text-[var(--color-text-secondary)]">
              Hub API → この画面
            </span>
          </div>
        </Card>
      )}

      {grouped.map(({ block, metrics }) => (
        <div key={block}>
          <h2
            className="mb-3 flex items-center gap-2 text-sm font-semibold"
            style={{ color: BLOCK_TINT[block] }}
          >
            {block}
            {tab === 'technical' && (
              <span className="font-mono text-xs font-normal text-[var(--color-text-muted)]">
                — {BLOCK_DB_SUMMARY[block]}
              </span>
            )}
          </h2>
          <div className="space-y-3">
            {metrics.map((def) =>
              tab === 'business' ? (
                <BusinessCard key={def.key} def={def} />
              ) : (
                <TechnicalCard key={def.key} def={def} />
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
